/**
 * Autofix MCP Tools — standalone registration
 *
 * Registers all autofix tools on an MCP Server instance.
 * Adapted from the desktop app's enterprise MCP server.
 */

import { z } from "zod";
import { execFileSync } from "child_process";
import { randomUUID } from "crypto";
import * as path from "path";
import * as fs from "fs";
import type { Server } from "@modelcontextprotocol/sdk/server/index.js";
import {
  CallToolRequestSchema,
  ListToolsRequestSchema,
} from "@modelcontextprotocol/sdk/types.js";
import type { AutofixBackendClient } from "./autofix-backend-client.js";
import {
  MCP_GET_BACKEND_LOGS,
  MCP_GET_EXCEPTIONS,
  MCP_GET_FRONTEND_LOGS,
  MCP_GET_NETWORK_REQUEST_LOGS,
  MCP_GET_NETWORK_HOPS,
  MCP_GET_PRINT_STATEMENTS,
  MCP_GET_USER_ACTIONS,
  MCP_GET_FRONTEND_DISPLAY_DATA,
  MCP_GET_ISSUE_OVERVIEW,
  MCP_CREATE_AUTOFIX_RUN,
  MCP_UPDATE_AUTOFIX_RUN,
  MCP_CREATE_PULL_REQUEST,
  MCP_GET_COMPANY_REPOS,
  MCP_GET_CAPTURED_FUNCTION_SPANS,
} from "./autofix-graphql-queries.js";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

// Run id resolution: a skill launch creates the run via autofix_start_run and
// stashes it here, so update/PR calls don't depend on the agent carrying the
// id through its context. Desktop/conductor launches set AUTOFIX_RUN_ID instead.
let activeRunId: string | null = null;

function resolveRunId(explicit?: string): string | null {
  return explicit || activeRunId || process.env.AUTOFIX_RUN_ID || null;
}

const MultiSessionInputSchema = z.object({
  sessionIds: z.array(z.string()).min(1).describe("Recording session IDs to query"),
  startTimestampMs: z.number().optional().describe("Filter: start timestamp in ms. Recommended for heavily-logged sessions to focus results and avoid truncation — get the window from get_issue_overview's telemetry window."),
  endTimestampMs: z.number().optional().describe("Filter: end timestamp in ms. Pair with startTimestampMs to scope a heavily-logged session."),
  companyId: z.number().optional().describe("Company ID (optional, defaults to authenticated user's company)"),
});

function buildInputVars(params: z.infer<typeof MultiSessionInputSchema>) {
  return {
    input: {
      sessionIds: params.sessionIds,
      startTimestampMs: params.startTimestampMs ?? null,
      endTimestampMs: params.endTimestampMs ?? null,
    },
    companyId: params.companyId ?? null,
  };
}

// Hard cap on tool response size. This MUST stay under Claude Code's PER-TOOL-RESULT
// token budget (~25k tokens): a result above it isn't shown inline — CC saves it to a
// file the agent then re-opens and skims, so the telemetry never lands. The old
// 150k-char cap (~37k tokens) was ABOVE that budget, so every large log response got
// file-dumped — defeating the cap entirely. ~32k chars ≈ ~8k tokens stays comfortably
// inline. Mostly a BACKSTOP now: the per-session row cap (100) already keeps thin-row,
// single-session responses well under this — 32k mainly bites fat-row tools (frontend
// console ~2k chars/row) and multi-session calls, where a tighter bound is what we want.
const RESPONSE_HARD_CAP_CHARS = 32_000;

interface ToolResponse {
  content: Array<{ type: string; text: string }>;
  isError?: boolean;
  _meta?: Record<string, any>;
}

function textResponse(text: string, itemCount?: number): ToolResponse {
  let truncated = false;
  let outputText = text;
  if (outputText.length > RESPONSE_HARD_CAP_CHARS) {
    // Keep the HEAD and the TAIL, not just the head: for session logs the failure
    // usually surfaces at the END, so a plain head-slice drops the most relevant lines.
    // For inputs far above the cap an arbitrary MIDDLE section is dropped.
    const headChars = Math.floor(RESPONSE_HARD_CAP_CHARS * 0.6);
    const tailChars = RESPONSE_HARD_CAP_CHARS - headChars;
    outputText = text.slice(0, headChars) +
      `\n\n---\n**Output truncated** (${text.length.toLocaleString()} chars exceeded ${RESPONSE_HARD_CAP_CHARS.toLocaleString()} char limit; ` +
      `showing the first and last portions — a middle section was dropped). Narrow with a time window ` +
      `(startTimestampMs/endTimestampMs, from get_issue_overview), a tighter filter, or fewer session IDs.\n---\n\n` +
      text.slice(text.length - tailChars);
    truncated = true;
    console.warn(`[autofix] Tool response truncated (head+tail): ${text.length} chars → ${RESPONSE_HARD_CAP_CHARS} chars`);
  }
  return {
    content: [{ type: "text", text: outputText }],
    _meta: {
      metrics: { outputSize: outputText.length, estimatedTokens: Math.ceil(outputText.length / 4), itemCount, truncated },
    },
  };
}

function errorResponse(message: string): ToolResponse {
  return { content: [{ type: "text", text: message }], isError: true };
}

// ── Size-safe formatting for captured function-spans ──
// Function spans carry the real runtime `arguments` and `return_value` of a
// backend call — the SDK caps each at ~1 MB, so a single span can dwarf Claude
// Code's per-tool-result token budget. The global `textResponse` cap is only a
// blunt head/tail backstop that mangles JSON mid-object, which is unacceptable
// for these (the demo's signal is exact args/returns). So we bound funcspans
// TWICE, before `textResponse` ever sees the string:
//   1. per-field — each args/return field is truncated to FIELD_CAP_CHARS with a
//      clear marker, so one fat payload can't blow the response and every other
//      field stays intact, readable JSON;
//   2. total — spans are rendered newest-first only while the running total stays
//      under FUNCSPAN_TOTAL_BUDGET_CHARS (headroom below the 32k global cap), so
//      the response never trips the JSON-mangling backstop. At least one span is
//      always shown (its two capped fields fit the budget by construction), and
//      an overflow note points at `limit` / a tighter capture for the rest.
// Small values (the smoking-gun numbers) are far under the field cap and pass
// through byte-exact — truncation only ever bites genuinely huge payloads.
const FUNCSPAN_FIELD_CAP_CHARS = 1_500;
const FUNCSPAN_TOTAL_BUDGET_CHARS = 28_000;

function formatFuncspanField(value: unknown): string {
  let json = JSON.stringify(value, null, 2);
  if (json === undefined) json = "null"; // undefined isn't valid JSON; spans use null
  if (json.length <= FUNCSPAN_FIELD_CAP_CHARS) return json;
  return (
    json.slice(0, FUNCSPAN_FIELD_CAP_CHARS) +
    `\n… (truncated; ${json.length.toLocaleString()} chars total — raw payload capped for inline display; narrow the capture to inspect the full value)`
  );
}

export interface CapturedFunctionSpan {
  timestamp: string;
  filePath: string;
  functionName: string;
  arguments: unknown;
  returnValue: unknown;
  durationNs: string | null;
}

/**
 * Render captured function-spans to size-safe markdown. Pure (no I/O) so it is
 * unit-testable; the handler wraps it with the query + empty-check + textResponse.
 * `spans` must be non-empty (the handler special-cases the empty result). Spans
 * are rendered newest-first only while the running total stays under
 * FUNCSPAN_TOTAL_BUDGET_CHARS; at least the newest is always emitted.
 */
export function renderCapturedFunctionSpans(spans: CapturedFunctionSpan[]): string {
  const target = `${spans[0].filePath}::${spans[0].functionName}`;
  const header = [
    `# Captured function-spans for \`${target}\``,
    `**${spans.length}** captured call(s), newest first. Each shows the real ` +
    `runtime arguments and return value of one execution.`,
    "",
  ].join("\n");

  const blocks: string[] = [];
  let used = header.length;
  spans.forEach((s, i) => {
    const block = [
      `## Call ${i + 1} — ${s.timestamp}`,
      ...(s.durationNs ? [`**duration_ns:** ${s.durationNs}`] : []),
      "**arguments:**",
      "```json",
      formatFuncspanField(s.arguments),
      "```",
      "**return_value:**",
      "```json",
      formatFuncspanField(s.returnValue),
      "```",
      "",
    ].join("\n");
    if (blocks.length > 0 && used + block.length > FUNCSPAN_TOTAL_BUDGET_CHARS) return;
    blocks.push(block);
    used += block.length;
  });

  const lines = [header, ...blocks];
  if (blocks.length < spans.length) {
    lines.push(
      `_Showing the newest ${blocks.length} of ${spans.length} captured spans ` +
      `(bounded to keep the response inline) — pass a smaller \`limit\`, or ` +
      `narrow the capture, to inspect the rest._`,
    );
  }
  return lines.join("\n");
}

// ---------------------------------------------------------------------------
// Register all tools on the MCP server
// ---------------------------------------------------------------------------

export function registerAutofixTools(server: Server, client: AutofixBackendClient): void {
  server.setRequestHandler(ListToolsRequestSchema, async () => ({
    tools: TOOL_DEFINITIONS,
  }));

  server.setRequestHandler(CallToolRequestSchema, async (request) => {
    const { name, arguments: args } = request.params;
    const handler = toolHandlers[name];
    if (!handler) {
      return errorResponse(`Unknown tool: ${name}`);
    }
    return handler(args, client);
  });
}

// ---------------------------------------------------------------------------
// Tool definitions
// ---------------------------------------------------------------------------

const TOOL_DEFINITIONS = [
  {
    name: "autofix_get_issue_overview",
    description: "Fetch the overview for a Sailfish issue. Returns the issue's identity — title, description, AI-generated description, and customer report (summary + conversation) — plus the captured exception message, stack trace, occurrence count, first/last seen dates, and associated recording session IDs (the exception fields are empty for behavioral issues). ALWAYS call this first when investigating an issue.",
    inputSchema: {
      type: "object",
      properties: {
        issueId: { type: "number", description: "Sailfish issue ID to fetch the overview for" },
        companyId: { type: "number", description: "Company ID (optional, defaults to authenticated user's company)" },
      },
      required: ["issueId"],
    },
  },
  {
    name: "autofix_get_backend_logs",
    description: "Fetch backend (server-side) logs for recording sessions. Returns log level, statement, and timestamp for each log entry.",
    inputSchema: {
      type: "object",
      properties: {
        sessionIds: { type: "array", items: { type: "string" }, description: "Recording session IDs to query" },
        startTimestampMs: { type: "number", description: "Filter: start timestamp in ms. Recommended for heavily-logged sessions to focus results and avoid truncation — get the window from get_issue_overview's telemetry window." },
        endTimestampMs: { type: "number", description: "Filter: end timestamp in ms. Pair with startTimestampMs to scope a heavily-logged session." },
        companyId: { type: "number", description: "Company ID (optional)" },
      },
      required: ["sessionIds"],
    },
  },
  {
    name: "autofix_get_exceptions",
    description: "Fetch all captured exceptions for recording sessions within an optional time window. Unlike autofix_get_issue_overview (which returns only the exception attributed to the issue), this surfaces every exception that fired in the session/window — including co-occurring or unattributed ones (symptom-vs-cause). Returns each exception's message, location (file:line in function), code, and stack trace.",
    inputSchema: {
      type: "object",
      properties: {
        sessionIds: { type: "array", items: { type: "string" }, description: "Recording session IDs to query" },
        startTimestampMs: { type: "number", description: "Filter: start timestamp in ms. Recommended for heavily-logged sessions to focus results and avoid truncation — get the window from get_issue_overview's telemetry window." },
        endTimestampMs: { type: "number", description: "Filter: end timestamp in ms. Pair with startTimestampMs to scope a heavily-logged session." },
        companyId: { type: "number", description: "Company ID (optional)" },
      },
      required: ["sessionIds"],
    },
  },
  {
    name: "autofix_get_frontend_logs",
    description: "Fetch frontend (console) logs for recording sessions. Returns log level, payload, trace, and timestamp.",
    inputSchema: {
      type: "object",
      properties: {
        sessionIds: { type: "array", items: { type: "string" }, description: "Recording session IDs to query" },
        startTimestampMs: { type: "number", description: "Filter: start timestamp in ms. Recommended for heavily-logged sessions to focus results and avoid truncation — get the window from get_issue_overview's telemetry window." },
        endTimestampMs: { type: "number", description: "Filter: end timestamp in ms. Pair with startTimestampMs to scope a heavily-logged session." },
        companyId: { type: "number", description: "Company ID (optional)" },
      },
      required: ["sessionIds"],
    },
  },
  {
    name: "autofix_get_network_request_logs",
    description: "Fetch network request logs (HTTP calls) for recording sessions. Returns URL, method, response code, success/error, and timing.",
    inputSchema: {
      type: "object",
      properties: {
        sessionIds: { type: "array", items: { type: "string" }, description: "Recording session IDs to query" },
        startTimestampMs: { type: "number", description: "Filter: start timestamp in ms. Recommended for heavily-logged sessions to focus results and avoid truncation — get the window from get_issue_overview's telemetry window." },
        endTimestampMs: { type: "number", description: "Filter: end timestamp in ms. Pair with startTimestampMs to scope a heavily-logged session." },
        companyId: { type: "number", description: "Company ID (optional)" },
      },
      required: ["sessionIds"],
    },
  },
  {
    name: "autofix_get_network_hops",
    description: "Fetch network hops (service-to-service calls) for recording sessions. Useful for distributed tracing.",
    inputSchema: {
      type: "object",
      properties: {
        sessionIds: { type: "array", items: { type: "string" }, description: "Recording session IDs to query" },
        startTimestampMs: { type: "number", description: "Filter: start timestamp in ms. Recommended for heavily-logged sessions to focus results and avoid truncation — get the window from get_issue_overview's telemetry window." },
        endTimestampMs: { type: "number", description: "Filter: end timestamp in ms. Pair with startTimestampMs to scope a heavily-logged session." },
        companyId: { type: "number", description: "Company ID (optional)" },
      },
      required: ["sessionIds"],
    },
  },
  {
    name: "autofix_get_print_statements",
    description: "Fetch print statements (stdout output) for recording sessions. Returns the printed output, source statement, and timestamp.",
    inputSchema: {
      type: "object",
      properties: {
        sessionIds: { type: "array", items: { type: "string" }, description: "Recording session IDs to query" },
        startTimestampMs: { type: "number", description: "Filter: start timestamp in ms. Recommended for heavily-logged sessions to focus results and avoid truncation — get the window from get_issue_overview's telemetry window." },
        endTimestampMs: { type: "number", description: "Filter: end timestamp in ms. Pair with startTimestampMs to scope a heavily-logged session." },
        companyId: { type: "number", description: "Company ID (optional)" },
      },
      required: ["sessionIds"],
    },
  },
  {
    name: "autofix_get_user_actions",
    description: "Fetch user interaction events (clicks, typing, navigation) for recording sessions.",
    inputSchema: {
      type: "object",
      properties: {
        sessionIds: { type: "array", items: { type: "string" }, description: "Recording session IDs to query" },
        startTimestampMs: { type: "number", description: "Filter: start timestamp in ms. Recommended for heavily-logged sessions to focus results and avoid truncation — get the window from get_issue_overview's telemetry window." },
        endTimestampMs: { type: "number", description: "Filter: end timestamp in ms. Pair with startTimestampMs to scope a heavily-logged session." },
        companyId: { type: "number", description: "Company ID (optional)" },
      },
      required: ["sessionIds"],
    },
  },
  {
    name: "autofix_get_frontend_display_data",
    description: "Fetch frontend display data snapshots for recording sessions. Returns compact JSON of the display state.",
    inputSchema: {
      type: "object",
      properties: {
        sessionIds: { type: "array", items: { type: "string" }, description: "Recording session IDs to query" },
        startTimestampMs: { type: "number", description: "Filter: start timestamp in ms. Recommended for heavily-logged sessions to focus results and avoid truncation — get the window from get_issue_overview's telemetry window." },
        endTimestampMs: { type: "number", description: "Filter: end timestamp in ms. Pair with startTimestampMs to scope a heavily-logged session." },
        companyId: { type: "number", description: "Company ID (optional)" },
      },
      required: ["sessionIds"],
    },
  },
  {
    name: "autofix_get_failed_container_details",
    description: "Get details about failed Kubernetes containers for a session. Note: requires kubectl access.",
    inputSchema: {
      type: "object",
      properties: {
        sessionId: { type: "string", description: "Recording session ID" },
        start: { type: "string", description: "Start time" },
        end: { type: "string", description: "End time" },
      },
      required: ["sessionId"],
    },
  },
  {
    name: "autofix_start_run",
    description: "Create the AutofixRun record for this investigation and return its run ID. Call this ONCE at the start of a skill-launched run, before reporting status — UNLESS AUTOFIX_RUN_ID is already set (desktop/conductor launches create the run for you). The returned run ID is what autofix_update_issue_status and autofix_create_pull_request expect.",
    inputSchema: {
      type: "object",
      properties: {
        issueId: { type: "number", description: "The issue being fixed" },
        companyId: { type: "number", description: "Company ID (optional)" },
      },
      required: ["issueId"],
    },
  },
  {
    name: "autofix_update_issue_status",
    description: "Update the status and investigation logs for an autofix run. Call this to report progress and persist investigation notes.",
    inputSchema: {
      type: "object",
      properties: {
        runId: { type: "string", description: "The autofix run ID (optional — defaults to the run from autofix_start_run, or AUTOFIX_RUN_ID)" },
        state: { type: "string", enum: ["running", "succeeded", "failed", "pr_awaiting_review"], description: "New state" },
        logs: { type: "string", description: "Investigation logs (markdown)" },
        branchUrl: { type: "string", description: "URL to pushed branch" },
        prUrl: { type: "string", description: "URL to pull request" },
        prProvider: { type: "string", enum: ["github", "bitbucket"], description: "VCS provider" },
        companyId: { type: "number", description: "Company ID (optional)" },
      },
      required: ["state"],
    },
  },
  {
    name: "autofix_create_pull_request",
    description: "Create a pull request via the company's GitHub/Bitbucket integration. The branch must already be pushed.",
    inputSchema: {
      type: "object",
      properties: {
        runId: { type: "string", description: "The autofix run ID (optional — defaults to the run from autofix_start_run, or AUTOFIX_RUN_ID)" },
        repoOwner: { type: "string", description: "Repository owner/organization" },
        repoName: { type: "string", description: "Repository name" },
        headBranch: { type: "string", description: "Branch with the fix" },
        title: { type: "string", description: "Pull request title" },
        body: { type: "string", description: "Pull request body (markdown)" },
        baseBranch: { type: "string", description: "Target branch (defaults to repo default)" },
        companyId: { type: "number", description: "Company ID (optional)" },
      },
      required: ["repoOwner", "repoName", "headBranch", "title"],
    },
  },
  {
    name: "autofix_clone_repos",
    description: "Clone all repositories for the current user's company into ~/git/sailfish-autofix/. Pulls latest if already cloned.",
    inputSchema: {
      type: "object",
      properties: {
        companyId: { type: "number", description: "Company ID (optional)" },
      },
    },
  },
  {
    name: "autofix_get_captured_function_spans",
    description:
      "Read back the runtime data captured by an armed backend function-span session — the real arguments and return values recorded each time the target function ran since capture started. Capture is armed by the conductor before this autofix run is launched; the sessionId is supplied to the run. The target file/function is recovered from the session, so pass only the sessionId. A session can arm several targets — optionally pass functionName (and filePath to disambiguate) to fetch just one instead of all. Returns empty if nothing was captured yet — the capture stays armed until its TTL, so check again later.",
    inputSchema: {
      type: "object",
      properties: {
        sessionId: {
          type: "string",
          description: "Backend function-span session ID provided by the conductor when capture was armed for this run.",
        },
        limit: {
          type: "number",
          description: "Max spans to fetch, newest first (default 50, capped at 200).",
        },
        functionName: {
          type: "string",
          description: "Optional: when the session armed several targets, fetch only spans for this function.",
        },
        filePath: {
          type: "string",
          description: "Optional: disambiguate functionName when the same name is armed in two files (exact file path).",
        },
        companyId: {
          type: "number",
          description: "Company ID (optional, defaults to authenticated user's company)",
        },
      },
      required: ["sessionId"],
    },
  },
];

// ---------------------------------------------------------------------------
// Tool handlers
// ---------------------------------------------------------------------------

type ToolHandler = (args: any, client: AutofixBackendClient) => Promise<ToolResponse>;

const toolHandlers: Record<string, ToolHandler> = {
  autofix_get_issue_overview: async (args, client) => {
    try {
      const data = await client.query<{
        mcpGetIssueOverview: {
          issueId: number; issueTitle: string; totalOccurrences: number;
          description: string | null; aiGeneratedDescription: string | null;
          customerSummary: string | null; customerConversation: string | null;
          firstSeen: string | null; lastSeen: string | null;
          exceptionMessage: string | null; location: string | null;
          stackFrames: Array<{ file: string | null; line: number | null; function: string | null; code: string | null; offender: boolean | null }> | null;
          sessionIds: string[]; wasCaught: boolean | null;
          startTimestampMs: number | null; endTimestampMs: number | null;
        };
      }>(MCP_GET_ISSUE_OVERVIEW, { issueId: args.issueId, companyId: args.companyId ?? null });

      const overview = data.mcpGetIssueOverview;
      const lines: string[] = [
        `# Issue Overview for Issue #${overview.issueId}`,
        `**Title:** ${overview.issueTitle}`,
        `**Total occurrences:** ${overview.totalOccurrences}${!overview.exceptionMessage ? " (no captured exception — this is an observation count for a behavioral/UX/discovery bug, not an error count)" : ""}`,
      ];
      if (overview.firstSeen) lines.push(`**First seen:** ${overview.firstSeen}`);
      if (overview.lastSeen) lines.push(`**Last seen:** ${overview.lastSeen}`);

      if (overview.description) {
        lines.push("", "## Human Description (human-written — more reliable, but not ground truth)", "", overview.description);
      }
      if (overview.aiGeneratedDescription) {
        lines.push("", "## AI Summary (auto-generated — a hypothesis, not fact: may be wrong or incomplete, and is derived from the approximate time window below, so it can inherit a wrong-window blind spot; verify against telemetry, do not assume correct)", "", overview.aiGeneratedDescription);
      }
      if (overview.customerSummary || overview.customerConversation) {
        lines.push("", "## Customer Report");
        if (overview.customerSummary) lines.push("", `**Summary:** ${overview.customerSummary}`);
        if (overview.customerConversation) lines.push("", "**Conversation:**", "", overview.customerConversation);
      }

      if (overview.exceptionMessage || overview.location || overview.wasCaught !== null) lines.push("", "## Exception");
      if (overview.exceptionMessage) lines.push(`**Message:** ${overview.exceptionMessage}`);
      if (overview.location) lines.push(`**Location:** \`${overview.location}\``);
      if (overview.wasCaught !== null) lines.push(`**Was caught:** ${overview.wasCaught}`);

      if (overview.stackFrames && overview.stackFrames.length > 0) {
        lines.push("", "## Stack Trace", "```");
        for (const frame of overview.stackFrames) {
          const parts: string[] = [];
          if (frame.file) parts.push(`file: ${frame.file}`);
          if (frame.line !== null) parts.push(`line: ${frame.line}`);
          if (frame.function) parts.push(`function: ${frame.function}`);
          if (frame.code) parts.push(`code: ${frame.code}`);
          if (frame.offender) parts.push(`offender: true`);
          lines.push(`  ${parts.join(", ")}`);
        }
        lines.push("```");
      }

      if (overview.sessionIds.length > 0) {
        lines.push("", "## Associated Session IDs", "", "**IMPORTANT: Fetch telemetry with these session IDs BEFORE reading code.**", "");
        lines.push("Session IDs to pass:");
        for (const sid of overview.sessionIds) lines.push(`- \`${sid}\``);
        if (overview.startTimestampMs && overview.endTimestampMs) {
          lines.push("", "**Time window** — our best guess of when the issue occurred; APPROXIMATE, not ground truth (human/auto-selected, often loose or even off):");
          lines.push(`- startTimestampMs: \`${overview.startTimestampMs}\``);
          lines.push(`- endTimestampMs: \`${overview.endTimestampMs}\``);
          lines.push("- Pass these to scope telemetry, but if the telemetry in this window does not show the reported failure, WIDEN your search — the reproduction may sit just outside it. Check the rest of the session, and you may check the same user's nearby sessions (drop or widen the timestamp filters). Don't over-extend or invent a cause: if the failure genuinely isn't in the telemetry, say so.");
        }
      } else {
        lines.push("", "_No recording sessions associated. Proceed with code analysis using the stack trace above._");
      }
      return textResponse(lines.join("\n"), overview.totalOccurrences);
    } catch (err: any) {
      return errorResponse(`Failed to fetch issue overview: ${err.message}`);
    }
  },

  autofix_get_backend_logs: async (args, client) => {
    try {
      const data = await client.query<{
        mcpGetBackendLogs: Array<{ sessionId: string; totalCount: number | null; logs: Array<{ level: string; statement: string; formattedTimestamp: string }> }>;
      }>(MCP_GET_BACKEND_LOGS, buildInputVars(args));
      const sessions = data.mcpGetBackendLogs;
      if (!sessions.length) return textResponse("No backend logs found.");
      const lines: string[] = ["# Backend Logs", ""];
      let total = 0;
      for (const s of sessions) {
        lines.push(`## Session: ${s.sessionId}`);
        if (!s.logs.length) { lines.push("_No logs_\n"); continue; }
        let shown = 0;
        for (const l of s.logs) { lines.push(`[${l.formattedTimestamp}] **${l.level}** ${l.statement}`); total++; shown++; }
        if (s.totalCount && s.totalCount > shown) {
          lines.push(`_Showing the newest ${shown} of ${s.totalCount} — narrow with startTimestampMs/endTimestampMs (from get_issue_overview) or a tighter filter to see earlier ones._`);
        }
        lines.push("");
      }
      return textResponse(lines.join("\n"), total);
    } catch (err: any) { return errorResponse(`Failed to fetch backend logs: ${err.message}`); }
  },

  autofix_get_exceptions: async (args, client) => {
    try {
      const data = await client.query<{
        mcpGetExceptions: Array<{ sessionId: string; totalCount: number | null; exceptions: Array<{ id: string; timestampMs: string; exceptionMessage: string | null; file: string | null; line: number | null; column: number | null; function: string | null; code: string | null; traceStack: Array<{ file: string | null; line: string | null; function: string | null; code: string | null; offender: boolean | null }> }> }>;
      }>(MCP_GET_EXCEPTIONS, buildInputVars(args));
      const sessions = data.mcpGetExceptions;
      if (!sessions.length) return textResponse("No exceptions found.");
      const lines: string[] = ["# Exceptions", ""];
      let total = 0;
      for (const s of sessions) {
        lines.push(`## Session: ${s.sessionId}`);
        if (!s.exceptions.length) { lines.push("_No exceptions_\n"); continue; }
        let shown = 0;
        for (const exc of s.exceptions) {
          const location = [exc.file, exc.line].filter((v) => v != null).join(":");
          const where = location ? `${location}${exc.function ? ` in ${exc.function}` : ""}` : exc.function ?? "";
          lines.push(`### [${exc.timestampMs}] ${exc.exceptionMessage ?? "(no message)"}`);
          if (where) lines.push(`Location: \`${where}\``);
          if (exc.code) lines.push(`Code: \`${exc.code}\``);
          if (exc.traceStack && exc.traceStack.length) {
            lines.push("```");
            for (const frame of exc.traceStack) {
              const parts: string[] = [];
              if (frame.file) parts.push(`file: ${frame.file}`);
              if (frame.line != null) parts.push(`line: ${frame.line}`);
              if (frame.function) parts.push(`function: ${frame.function}`);
              if (frame.code) parts.push(`code: ${frame.code}`);
              if (frame.offender) parts.push("offender: true");
              lines.push(`  ${parts.join(", ")}`);
            }
            lines.push("```");
          }
          lines.push("");
          total++;
          shown++;
        }
        if (s.totalCount && s.totalCount > shown) {
          lines.push(`_Showing the newest ${shown} of ${s.totalCount} — narrow with startTimestampMs/endTimestampMs (from get_issue_overview) or a tighter window to see earlier ones._`);
        }
      }
      return textResponse(lines.join("\n"), total);
    } catch (err: any) { return errorResponse(`Failed to fetch exceptions: ${err.message}`); }
  },

  autofix_get_frontend_logs: async (args, client) => {
    try {
      const data = await client.query<{
        mcpGetFrontendLogs: Array<{ sessionId: string; totalCount: number | null; logs: Array<{ level: string; trace: string; payload: string; formattedTimestamp: string }> }>;
      }>(MCP_GET_FRONTEND_LOGS, buildInputVars(args));
      const sessions = data.mcpGetFrontendLogs;
      if (!sessions.length) return textResponse("No frontend logs found.");
      const lines: string[] = ["# Frontend (Console) Logs", ""];
      let total = 0;
      for (const s of sessions) {
        lines.push(`## Session: ${s.sessionId}`);
        if (!s.logs.length) { lines.push("_No logs_\n"); continue; }
        let shown = 0;
        for (const l of s.logs) { lines.push(`[${l.formattedTimestamp}] **${l.level}** ${l.payload}`); if (l.trace) lines.push(`  Trace: ${l.trace}`); total++; shown++; }
        if (s.totalCount && s.totalCount > shown) {
          lines.push(`_Showing the newest ${shown} of ${s.totalCount} — narrow with startTimestampMs/endTimestampMs (from get_issue_overview) or a tighter filter to see earlier ones._`);
        }
        lines.push("");
      }
      return textResponse(lines.join("\n"), total);
    } catch (err: any) { return errorResponse(`Failed to fetch frontend logs: ${err.message}`); }
  },

  autofix_get_network_request_logs: async (args, client) => {
    try {
      const data = await client.query<{
        mcpGetNetworkRequestLogs: Array<{ sessionId: string; totalCount: number | null; requests: Array<{ url: string; method: string; responseCode: number; success: boolean; error: string | null; formattedTimestampStart: string; formattedTimestampEnd: string; requestId: string; hasGraphqlErrors: boolean | null; durationMs: number | null; graphqlOperationName: string | null; graphqlOperationType: string | null; responseBody: string | null; requestBody: string | null }> }>;
      }>(MCP_GET_NETWORK_REQUEST_LOGS, buildInputVars(args));
      const sessions = data.mcpGetNetworkRequestLogs;
      if (!sessions.length) return textResponse("No network requests found.");
      const lines: string[] = ["# Network Request Logs", ""];
      let total = 0;
      for (const s of sessions) {
        lines.push(`## Session: ${s.sessionId}`);
        if (!s.requests.length) { lines.push("_No requests_\n"); continue; }
        let shown = 0;
        for (const r of s.requests) {
          const gqlFailed = r.hasGraphqlErrors ? " GRAPHQL_ERROR" : "";
          const status = r.success ? `${r.responseCode}${gqlFailed}` : `${r.responseCode} FAILED${gqlFailed}`;
          const duration = r.durationMs != null ? ` (${r.durationMs}ms)` : "";
          lines.push(`[${r.formattedTimestampStart}] **${r.method}** ${r.url} → ${status}${duration}`);
          if (r.graphqlOperationName) {
            const opType = r.graphqlOperationType ? ` ${r.graphqlOperationType}` : "";
            lines.push(`  GraphQL:${opType} ${r.graphqlOperationName}`);
          }
          if (r.error) lines.push(`  Error: ${r.error}`);
          // Bodies are populated for failing requests only (truncated server-side).
          if (r.requestBody) lines.push(`  Request body: ${r.requestBody}`);
          if (r.responseBody) lines.push(`  Response body: ${r.responseBody}`);
          total++;
          shown++;
        }
        if (s.totalCount && s.totalCount > shown) {
          lines.push(`_Showing the newest ${shown} of ${s.totalCount} — narrow with startTimestampMs/endTimestampMs (from get_issue_overview) or a tighter filter to see earlier ones._`);
        }
        lines.push("");
      }
      return textResponse(lines.join("\n"), total);
    } catch (err: any) { return errorResponse(`Failed to fetch network request logs: ${err.message}`); }
  },

  autofix_get_network_hops: async (args, client) => {
    try {
      const data = await client.query<{
        mcpGetNetworkHops: Array<{ sessionId: string; totalCount: number | null; hops: Array<{ line: number; column: number; name: string; formattedTimestamp: string; entrypoint: string }> }>;
      }>(MCP_GET_NETWORK_HOPS, buildInputVars(args));
      const sessions = data.mcpGetNetworkHops;
      if (!sessions.length) return textResponse("No network hops found.");
      const lines: string[] = ["# Network Hops", ""];
      let total = 0;
      for (const s of sessions) {
        lines.push(`## Session: ${s.sessionId}`);
        if (!s.hops.length) { lines.push("_No hops_\n"); continue; }
        let shown = 0;
        for (const h of s.hops) { lines.push(`[${h.formattedTimestamp}] **${h.name}** at ${h.entrypoint} (${h.line}:${h.column})`); total++; shown++; }
        if (s.totalCount && s.totalCount > shown) {
          lines.push(`_Showing the newest ${shown} of ${s.totalCount} — narrow with startTimestampMs/endTimestampMs (from get_issue_overview) or a tighter filter to see earlier ones._`);
        }
        lines.push("");
      }
      return textResponse(lines.join("\n"), total);
    } catch (err: any) { return errorResponse(`Failed to fetch network hops: ${err.message}`); }
  },

  autofix_get_print_statements: async (args, client) => {
    try {
      const data = await client.query<{
        mcpGetPrintStatements: Array<{ sessionId: string; totalCount: number | null; printStatements: Array<{ output: string; statement: string; formattedTimestamp: string }> }>;
      }>(MCP_GET_PRINT_STATEMENTS, buildInputVars(args));
      const sessions = data.mcpGetPrintStatements;
      if (!sessions.length) return textResponse("No print statements found.");
      const lines: string[] = ["# Print Statements", ""];
      let total = 0;
      for (const s of sessions) {
        lines.push(`## Session: ${s.sessionId}`);
        if (!s.printStatements.length) { lines.push("_No print statements_\n"); continue; }
        let shown = 0;
        for (const p of s.printStatements) { lines.push(`[${p.formattedTimestamp}] ${p.output}`); if (p.statement) lines.push(`  Statement: ${p.statement}`); total++; shown++; }
        if (s.totalCount && s.totalCount > shown) {
          lines.push(`_Showing the newest ${shown} of ${s.totalCount} — narrow with startTimestampMs/endTimestampMs (from get_issue_overview) or a tighter filter to see earlier ones._`);
        }
        lines.push("");
      }
      return textResponse(lines.join("\n"), total);
    } catch (err: any) { return errorResponse(`Failed to fetch print statements: ${err.message}`); }
  },

  autofix_get_user_actions: async (args, client) => {
    try {
      const data = await client.query<{
        mcpGetUserActions: Array<{ sessionId: string; pageVisitUserActions: Array<{ userActions: Array<{ actionType: string; textContent: string; dotPath: string; timestamp: string }> }> }>;
      }>(MCP_GET_USER_ACTIONS, buildInputVars(args));
      const sessions = data.mcpGetUserActions;
      if (!sessions.length) return textResponse("No user actions found.");
      const lines: string[] = ["# User Interactions", ""];
      let total = 0;
      for (const s of sessions) {
        lines.push(`## Session: ${s.sessionId}`);
        if (!s.pageVisitUserActions.length) { lines.push("_No user actions_\n"); continue; }
        for (const pv of s.pageVisitUserActions) {
          for (const a of pv.userActions) { lines.push(`[${a.timestamp}] **${a.actionType}** "${a.textContent}" @ ${a.dotPath}`); total++; }
        }
        lines.push("");
      }
      return textResponse(lines.join("\n"), total);
    } catch (err: any) { return errorResponse(`Failed to fetch user actions: ${err.message}`); }
  },

  autofix_get_frontend_display_data: async (args, client) => {
    try {
      const data = await client.query<{
        mcpGetFrontendDisplayData: Array<{ sessionId: string; displayData: Array<{ pageVisitId: string; compactJson: string }> }>;
      }>(MCP_GET_FRONTEND_DISPLAY_DATA, buildInputVars(args));
      const sessions = data.mcpGetFrontendDisplayData;
      if (!sessions.length) return textResponse("No frontend display data found.");
      const lines: string[] = ["# Frontend Display Data", ""];
      let total = 0;
      for (const s of sessions) {
        lines.push(`## Session: ${s.sessionId}`);
        if (!s.displayData.length) { lines.push("_No display data_\n"); continue; }
        for (const d of s.displayData) { lines.push(`### Page Visit: ${d.pageVisitId}`, "```json", d.compactJson, "```"); total++; }
        lines.push("");
      }
      return textResponse(lines.join("\n"), total);
    } catch (err: any) { return errorResponse(`Failed to fetch frontend display data: ${err.message}`); }
  },

  autofix_get_failed_container_details: async () => {
    return textResponse(
      "Container failure details require kubectl access to the Kubernetes cluster. " +
      "Use the web dashboard or Prefect pipeline to inspect container failures."
    );
  },

  autofix_start_run: async (args, client) => {
    // Skill-launched runs have no desktop app / conductor to create the row,
    // so the skill creates it here. run_id is ours to mint; the `skill-`
    // prefix makes the origin obvious in logs.
    const runId = `skill-${args.issueId}-${randomUUID()}`;
    try {
      const data = await client.query<{
        mcpCreateDesktopAutofixRun: { id: string; runId: string; issueId: number; state: string; source: string };
      }>(MCP_CREATE_AUTOFIX_RUN, {
        issueId: args.issueId, runId, runSource: "skill",
        companyId: args.companyId ?? null,
      });
      const run = data.mcpCreateDesktopAutofixRun;
      activeRunId = run.runId;
      return textResponse(`Autofix run created: ${run.runId} (issue #${run.issueId}, state=${run.state}). It is now the active run for this session — autofix_update_issue_status and autofix_create_pull_request use it automatically.`);
    } catch (err: any) { return errorResponse(`Failed to create autofix run: ${err.message}`); }
  },

  autofix_update_issue_status: async (args, client) => {
    const runId = resolveRunId(args.runId);
    if (!runId) return errorResponse("No autofix run id available. Call autofix_start_run first, or ensure AUTOFIX_RUN_ID is set.");
    try {
      const data = await client.query<{
        mcpUpdateAutofixRun: { id: string; runId: string; issueId: number; state: string };
      }>(MCP_UPDATE_AUTOFIX_RUN, {
        runId, state: args.state,
        logs: args.logs ?? null, branchUrl: args.branchUrl ?? null,
        prUrl: args.prUrl ?? null, prProvider: args.prProvider ?? null,
        companyId: args.companyId ?? null,
      });
      const run = data.mcpUpdateAutofixRun;
      return textResponse(`Autofix run ${run.runId} updated: state=${run.state}, issue=#${run.issueId}`);
    } catch (err: any) { return errorResponse(`Failed to update autofix run: ${err.message}`); }
  },

  autofix_create_pull_request: async (args, client) => {
    const runId = resolveRunId(args.runId);
    if (!runId) return errorResponse("No autofix run id available. Call autofix_start_run first, or ensure AUTOFIX_RUN_ID is set.");
    const provider = args.provider ?? "github";
    const branchUrl = provider === "bitbucket"
      ? `https://bitbucket.org/${args.repoOwner}/${args.repoName}/branch/${args.headBranch}`
      : `https://github.com/${args.repoOwner}/${args.repoName}/tree/${args.headBranch}`;
    try {
      const data = await client.query<{
        mcpCreatePullRequest: { id: string; runId: string; issueId: number; state: string; prUrl: string | null; prProvider: string | null };
      }>(MCP_CREATE_PULL_REQUEST, {
        runId, repoOwner: args.repoOwner, repoName: args.repoName,
        headBranch: args.headBranch, title: args.title, body: args.body ?? "",
        baseBranch: args.baseBranch ?? null, companyId: args.companyId ?? null,
      });
      const run = data.mcpCreatePullRequest;
      return textResponse(`PR created for autofix run ${run.runId} (issue #${run.issueId}).\nPR: ${run.prUrl} (${run.prProvider})\nState: ${run.state}`);
    } catch (err: any) {
      try {
        await client.query(MCP_UPDATE_AUTOFIX_RUN, {
          runId, state: "succeeded", logs: null,
          branchUrl, prUrl: null, prProvider: provider, companyId: args.companyId ?? null,
        });
        return textResponse(`PR creation failed (${err.message}), but branch was pushed.\nBranch: ${branchUrl}`);
      } catch (updateErr: any) {
        return errorResponse(`PR creation failed: ${err.message}\nBranch: ${branchUrl}`);
      }
    }
  },

  autofix_clone_repos: async (args, client) => {
    const AUTOFIX_REPOS_DIR = path.join(process.env.HOME || process.env.USERPROFILE || "/tmp", "git", "sailfish-autofix");
    try {
      const data = await client.query<{
        mcpGetCompanyRepos: Array<{ gitOrg: string; gitRepo: string; gitProvider: string }>;
      }>(MCP_GET_COMPANY_REPOS, { companyId: args.companyId ?? null });

      const repos: Array<{ name: string; urls: string[]; provider: string; account: string }> = [];
      const seen = new Set<string>();
      for (const r of data.mcpGetCompanyRepos) {
        const key = `${r.gitOrg}/${r.gitRepo}`.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        // Only github.com and bitbucket.org can be addressed purely client-side.
        // GitLab (incl. self-hosted) would need the integration's host, which
        // isn't available here, and isn't a live provider yet — flag it
        // explicitly rather than mis-cloning it against github.com.
        const provider = r.gitProvider.toLowerCase();
        const host =
          provider === "github" ? "github.com" :
          provider === "bitbucket" ? "bitbucket.org" :
          null;
        if (!host) {
          repos.push({ name: r.gitRepo, urls: [], provider, account: r.gitOrg });
          continue;
        }
        const sshUrl = `git@${host}:${r.gitOrg}/${r.gitRepo}.git`;
        const httpsUrl = `https://${host}/${r.gitOrg}/${r.gitRepo}.git`;
        // Try both clean auth methods so the clone works with whatever the
        // developer has — an SSH key, then HTTPS via their git credential
        // helper. Never embed a token in the URL. GitHub goes SSH-first,
        // Bitbucket HTTPS-first, matching each provider's common setup.
        const urls = provider === "bitbucket" ? [httpsUrl, sshUrl] : [sshUrl, httpsUrl];
        repos.push({ name: r.gitRepo, urls, provider, account: r.gitOrg });
      }

      if (!repos.length) {
        return textResponse("No repositories found. Ensure your company has GitHub or Bitbucket integrations in Sailfish.");
      }

      fs.mkdirSync(AUTOFIX_REPOS_DIR, { recursive: true });

      const cloneEnv: Record<string, string> = { ...process.env as Record<string, string>, GIT_TERMINAL_PROMPT: "0" };
      if (process.env.SSH_AUTH_SOCK) cloneEnv.SSH_AUTH_SOCK = process.env.SSH_AUTH_SOCK;

      const results: string[] = [`# Repository Setup`, ``, `Base directory: \`${AUTOFIX_REPOS_DIR}\``, "", "## Repositories", ""];
      let cloned = 0, skipped = 0, failed = 0;

      for (const repo of repos) {
        const repoDir = path.join(AUTOFIX_REPOS_DIR, repo.name);
        if (fs.existsSync(path.join(repoDir, ".git"))) {
          results.push(`- **${repo.name}**: already exists, skipped`);
          skipped++;
          continue;
        }
        if (!repo.urls.length) {
          results.push(`- **${repo.name}**: NOT SUPPORTED — provider '${repo.provider}' can't be cloned by this tool yet; clone it manually.`);
          failed++;
          continue;
        }
        const tmpDir = path.join(AUTOFIX_REPOS_DIR, `.tmp-clone-${repo.name}-${Date.now()}`);
        let cloneOk = false;
        let lastErr = "";
        for (const url of repo.urls) {
          try {
            execFileSync("git", ["clone", "--depth", "1", url, tmpDir], { timeout: 120_000, stdio: "pipe", env: cloneEnv });
            cloneOk = true;
            break;
          } catch (attemptErr: any) {
            lastErr = attemptErr.stderr?.toString()?.trim().split("\n").pop() || attemptErr.message;
            // git may leave a partial dir on a failed clone; clear it before the next scheme.
            fs.rmSync(tmpDir, { recursive: true, force: true });
          }
        }
        if (cloneOk) {
          fs.renameSync(tmpDir, repoDir);
          results.push(`- **${repo.name}**: cloned`);
          cloned++;
        } else {
          results.push(`- **${repo.name}**: FAILED — ${lastErr}`);
          failed++;
        }
      }

      results.push("", `**Summary:** ${cloned} cloned, ${skipped} existed, ${failed} failed (${repos.length} total)`);
      return textResponse(results.join("\n"), repos.length);
    } catch (err: any) {
      return errorResponse(`Failed to clone repositories: ${err.message}`);
    }
  },

  autofix_get_captured_function_spans: async (args, client) => {
    try {
      const data = await client.query<{
        mcpGetCapturedFunctionSpans: Array<{
          id: string;
          timestamp: string;
          filePath: string;
          functionName: string;
          arguments: unknown;
          returnValue: unknown;
          durationNs: string | null;
          sessionId: string;
          serviceUuid: string;
        }>;
      }>(MCP_GET_CAPTURED_FUNCTION_SPANS, {
        sessionId: args.sessionId,
        companyId: args.companyId ?? null,
        limit: args.limit ?? null,
        functionName: args.functionName ?? null,
        filePath: args.filePath ?? null,
      });

      const spans = data.mcpGetCapturedFunctionSpans ?? [];
      if (spans.length === 0) {
        // Distinguish "nothing captured yet" (worth retrying) from "the focus
        // filter matched no armed target" (retrying won't help — fix the name).
        if (args.functionName || args.filePath) {
          const filter = [
            args.functionName ? `functionName="${args.functionName}"` : null,
            args.filePath ? `filePath="${args.filePath}"` : null,
          ].filter(Boolean).join(", ");
          return textResponse(
            `No captured function-spans matched ${filter} in this session. Either that ` +
            "target isn't among the session's armed functions (the name/path must match " +
            "the armed target exactly), or nothing has been captured for it yet. Re-check " +
            "the name, or drop the filter to see every armed target.",
            0,
          );
        }
        return textResponse(
          "No captured function-spans yet for this session — the target function " +
          "has not been called (or recorded) since capture was armed. The capture " +
          "stays armed until its TTL; check again later.",
          0,
        );
      }
      return textResponse(renderCapturedFunctionSpans(spans), spans.length);
    } catch (err: any) {
      return errorResponse(`Failed to fetch captured function-spans: ${err.message}`);
    }
  },
};
